/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { Injectable } from '@angular/core';
import { JwtModel, UserSessionModel } from '../../models';
import { LicenceService } from '../../../modules/loadbalancer/shared/services/utilities';
import { LogService } from './log.service';
import { LoginModel } from '../../../modules/loadbalancer/shared/models';
import { ModalService } from '../component/modal/modal.service';
import { NotifyService } from './notify.service';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { TokenOptions, TokenStatus } from '../../enums';
import { UtilsService } from './utils.service';
import { environment, ModulesEnum } from '../../../../environments/environment';

/**
 * User service which acts as an observable such that any changes can be monitored in real time throughout our client.
 * This also acts as a service, so we can get local user information at any point.
 */
@Injectable()
export class UserService {
    /**
     * Our event emitter to let components know log out was called.
     */
    public logOutEmitter: Subject<boolean> = new Subject<boolean>();

    /**
     * The logged-in user model object.
     */
    private _user: UserSessionModel = <UserSessionModel>{};

    /**
     * To store the redirect location for use with the getter and setter.
     */
    private _redirectLocation: string;

    constructor(
        private licenceService: LicenceService,
        private logService: LogService,
        private modalService: ModalService,
        private notifyService: NotifyService,
        private router: Router,
        private utilsService: UtilsService
    ) {
    }

    /**
     * Get the Modules ENUM, so we can use it in the view.
     */
    get modulesEnum(): any {
        return ModulesEnum;
    }

    /**
     * Set the redirectLocation if there is one.
     * @param url
     */
    set redirectLocation(url) {
        this._redirectLocation = url;
    }

    /**
     * Get stored the redirectLocation if there is one.
     */
    get redirectLocation() {
        return this._redirectLocation;
    }

    /**
     * Set the user in this service and updates the local storage.
     * @param user
     */
    set user(user: UserSessionModel) {
        this._user = user;
        this.updateUserLocalStorage();
    }

    /**
     * Get the user.
     */
    get user() {
        return this._user;
    }

    /**
     * Set up user.
     * @param userDetails
     * @param data
     */
    public login(userDetails: LoginModel, data: any): void {
        this.setupUserDetails(userDetails, data);
        this.updateUserLocalStorage();
        this.setupIntercom();
    }

    /**
     * Logout user by clearing out the local set variable for lbUser.
     */
    public logout(redirectToLogin: boolean = true): void {
        this.logService.debug('UserService:logout()', 'User logged out & local storage cleared.');

        // Tell all services subscribed that we are logging out.
        this.logOutEmitter.next(true);

        // Clear all notifications so LB notification don't show on CM
        this.notifyService.clearAll();
        this.modalService.closeAll();

        // Clear the user-name/email address from intercom.
        this.restartIntercom();

        // Clear local storage
        this.clearLocalStorage();
        this.clearSessionStorage();

        // Redirect
        if (redirectToLogin) {
            this.router.navigateByUrl('/login').then().catch();
        }
    }

    /**
     * Clear local storage.
     */
    public clearLocalStorage(): void {
        localStorage.removeItem('lbUser');
        localStorage.removeItem('lbServices');
        localStorage.removeItem('lb-no-access');
        localStorage.removeItem('licence');
        localStorage.removeItem('drawerState');
    }

    /**
     * Clear session storage.
     */
    public clearSessionStorage(): void {
        sessionStorage.removeItem('orgKey');
        sessionStorage.removeItem('userKey');
    }

    /**
     * Function to be used to grab the local user model.
     * @param property
     */
    public getUser(property?: string): UserSessionModel | any {
        if (property) {
            if (this.user.hasOwnProperty(property)) {
                return this.user[property];
            } else {
                this.logService.debug('UserService: getUser', `Property ${property} not found in userService`);
            }
        } else {
            return this.user;
        }
    }

    /**
     * Function to used to set the local user model.
     * @param property
     * @param value
     */
    public setUserValueByProperty(property: string, value: string | number): void {
        if (this.user.hasOwnProperty(property)) {
            this.user[property] = value;
        } else {
            this.user[property] = value;
            this.logService.debug('UserService: setUserValueByProperty', 'New user property created');
        }

        this.updateUserLocalStorage();
    }

    /**
     * Format user details.
     * @param user
     * @param data
     * @private
     */
    public setupUserDetails(user: any, data: any): void {
        const decodedToken = this.utilsService.decodeJWT(data.token);

        this.user = {
            email: user.email,
            id: this.getId(data, decodedToken),
            name: this.getName(data),
            permissions: decodedToken.permissions,
            applianceId: data.appliance_id,
            applianceVersion: data.appliance_version,
            JWT: data.token,
            timestampJWT: Math.floor(Date.now() / 1000),
            applianceAddress: user.applianceAddress,
            appliancePort: user.appliancePort,
            selected: true,
            language: user.language,
            organisationId: decodedToken.orgID,
            ssoLogin: user.ssoLogin,
            otpEnabled: user.otpEnabled,
            ssoProvider: decodedToken.ssoProvider || null,
            tfaType: data.tfaType
        };
    }

    /**
     * Get User ID.
     * @param data
     * @param decodedToken
     * @private
     */
    private getId(data: any, decodedToken: any): string {
        if (data.user?.id) {
            return data.user.id.toString();
        } else if (decodedToken.id) {
            return decodedToken.id.toString();
        } else {
            this.logService.error('UserService:getId', 'Unable to get id from either data object');
        }
    }

    /**
     * Get name from data object.
     * @param data
     * @private
     */
    private getName(data: any): string {
        if (data.name) {
            return data.name;
        } else if (data.user?.firstname && data.user?.lastname) {
            return data.user.firstname + ' ' + data.user.lastname;
        } else {
            return null;
        }
    }

    /**
     * Update the local stored variable for lbUser.
     */
    public updateUserLocalStorage(): boolean {
        this.logService.debug('UserService', 'Updated user local storage', { user: this.user });

        // set the users permitted resources into local storage
        localStorage.setItem('lbUser', JSON.stringify(this.user));

        return true;
    }

    /**
     * Returns the local set variable for lbUser.
     */
    public hasLocalStorage(): boolean {
        // Get user from local storage
        const lbUser = localStorage.getItem('lbUser');
        let localUser = null;
        if (lbUser && lbUser !== 'undefined') {
            localUser = JSON.parse(lbUser);
        }
        if (!localUser) {
            return false;
        }
        // Get service statuses from local storage
        const lbServices = localStorage.getItem('lbServices');
        let serviceStatuses = null;
        if (lbServices && lbServices !== 'undefined') {
            serviceStatuses = JSON.parse(lbServices);
        }
        // Get the licence information from local storage
        const licence = localStorage.getItem('licence');
        let licenceDetails = null;
        if (licence && licence !== 'undefined') {
            licenceDetails = JSON.parse(licence);
        }

        // Log our findings
        this.logService.debug(
            'UserService',
            'Get local storage',
            {
                user: localUser,
                services: serviceStatuses,
                licence: licenceDetails
            }
        );

        // Validation
        // Note: we need at least a correct user object.
        this.user = localUser;
        this.licenceService.licenceObj = licenceDetails;

        return true;
    }

    /**
     * Return the 10th the token life or 30 seconds which is higher.
     */
    public getTokenCheckInterval(): number {
        if (!this.userObjectMatchesModule()) {
            this.logout();
        }

        // Check to see if we have a stored JWT
        if (!this.user.JWT) {
            this.logService.debug('UserService:getTokenCheckInterval()',
                'Unable to get JWT from local user object.', this.user);
            // Attempt to reload from local storage
            this.hasLocalStorage();

            // Check again, if all fails logout and try again
            if (!this.user.JWT) {
                this.logout();
            }

            return TokenOptions.INTERVAL;
        }
        // get token info
        const jwtInfo = this.utilsService.decodeJWT(this.user.JWT);

        if (!jwtInfo) {
            return TokenOptions.INTERVAL;
        }

        const startTime = jwtInfo.gen ? jwtInfo.gen : jwtInfo.iat;
        const refreshInterval = (jwtInfo.exp - startTime) / TokenOptions.INTERVAL;

        if (refreshInterval < TokenOptions.INTERVAL) {
            return TokenOptions.INTERVAL;
        }

        return refreshInterval;
    }

    /**
     * Check if the token is going to expire with in 1/3 of the different between gen and expire time.
     */
    public tokenStatus(): TokenStatus {
        /*
         * get the difference between client and server timestamps apply the difference to expire time workout the 1/3 of the different
         * between gen and expire time check if under the time and not expired.
         */
        if (!this.user || !this.user.JWT) {
            this.logService.info('tokenStatus()', 'Token invalid');

            return TokenStatus.INVALID;
        }

        // get token info
        const jwtInfo: JwtModel = this.utilsService.decodeJWT(this.user.JWT);

        if (!jwtInfo) {
            this.logService.info('tokenStatus()', 'Token invalid');

            return TokenStatus.INVALID;
        }

        // adjust expired time to allow clock differences
        const startTime = jwtInfo.gen ? jwtInfo.gen : jwtInfo.iat;
        const clockDiff = startTime - this.user.timestampJWT;
        const expireTime = jwtInfo.exp - clockDiff;

        const timeNow = Math.floor(Date.now() / 1000);

        // Check if token has expired (30sec leeway)
        if (expireTime <= (timeNow + 30)) {
            this.logService.info('tokenStatus()', 'Token expired');

            return TokenStatus.EXPIRED;
        }

        // get third of the token length
        const refreshInterval = Math.floor((jwtInfo.exp - startTime) / 3);

        if (expireTime <= (timeNow + refreshInterval)) {
            this.logService.info('tokenStatus()', 'Token need to be refreshed.');

            return TokenStatus.REFRESH;
        }

        return TokenStatus.OK;
    }

    /**
     * Set up intercom with user parameters.
     * @private
     */
    private setupIntercom(): void {
        // Setup intercom user attrs.
        (<any>window).Intercom('boot', {
            /* eslint-disable-next-line */
            app_id: 'su2x8ti6',
            // TODO 05/06/25 - This has been removed as part of the Intercom bug - "email: userDetails.email || ''",
            // TODO in order for the email address to work we need to provide a JWT for intercom.
        });
    }

    /**
     * Restart intercom with no parameters.
     * @private
     */
    private restartIntercom() {
        (<any>window).Intercom('shutdown');
        (<any>window).Intercom('boot');
    }

    /**
     * Check if the set module matches with the stored user object.
     */
    public userObjectMatchesModule(): boolean {
        const lbUser = JSON.parse(localStorage.getItem('lbUser'));

        return (this.modulesEnum[environment.module] === 'LOADBALANCER' && lbUser?.applianceId) ||
            (this.modulesEnum[environment.module] === 'CENTRALISED_MANAGEMENT' && lbUser?.organisationId);
    }

    /**
     * Create colour hex from string.
     * @param str
     */
    public createCodeHexFromString = (str: string) => {
        let hash = 0;
        str.split('').forEach(char => {
            // eslint-disable-next-line no-bitwise
            hash = char.charCodeAt(0) + ((hash << 5) - hash);
        });
        let colour = '#';
        for (let i = 0; i < 3; i++) {
            // eslint-disable-next-line no-bitwise
            const value = (hash >> (i * 8)) & 0xff;
            colour += value.toString(16).padStart(2, '0');
        }

        return colour;
    };

    /**
     * Create user avatar from names.
     * @param firstName
     * @param lastName
     */
    public createUserAvatarFromNames(firstName: string, lastName: string): {
        color: string,
        initials: string,
        fullName: string
    } {
        const initials = firstName.charAt(0) + lastName.charAt(0);
        const color = this.createCodeHexFromString(firstName + lastName);

        return {
            color,
            initials,
            fullName: `${firstName} ${lastName}`
        };
    }
}
